import { FormInstance, Tooltip, Typography } from "antd";
import { Dayjs } from "dayjs";
import { QueryOperation, SearchQuery } from "../code/search-query/search-query";
import { buildSearchQuery, comboboxDisplay, dateRangeValue, QueryFieldMapping } from "../code/search-query/query-mapper";
import { DataColumn } from "../controls/Data.default";
import { DynamicSchema } from "../controls/DynamicForm.default";
import { ActionLog } from "../repository/data.default";
import { nameOf } from "../utils/typescript";
import { InputObject } from "../controls/default";
import {
  createSearchRequest,
  createScopedFilterRequest,
} from "../hooks/useSearchRequest";
import { getCompanies, getPlatformUsers, getTenants } from "../repository/data.repository";

export interface ActionLogsForm {
  readonly actionDescription: string;
  readonly user: InputObject;
  readonly company: InputObject;
  readonly tenant: InputObject;
  readonly userUid: string;
  readonly companyUid: string;
  readonly tenantUid: string;
  readonly ipAddress: string;
  readonly userAgent: string;
  readonly timestamp: Dayjs[];
}

// Long single-line values (e.g. User Agent) — truncate in-cell with a full-text tooltip on hover.
const ellipsisCell = (value?: string) =>
  value ? (
    <Tooltip title={value}>
      <Typography.Text className="block truncate">{value}</Typography.Text>
    </Tooltip>
  ) : (
    "—"
  );

export const ActionLogsViewColumns: DataColumn[] = [
  {
    title: "Action Description",
    key: nameOf<ActionLog>("actionDescription"),
    width: 192,
    sort: true,
    defaultSort: "asc",
    fixed: "left",
  },
  {
    title: "User Name",
    key: nameOf<ActionLog>("userUserName"),
    width: 192,
    sort: true,
  },
  {
    title: "IP Address",
    key: nameOf<ActionLog>("ipAddress"),
    width: 320,
    sort: true,
  },
  {
    title: "User Agent",
    key: nameOf<ActionLog>("userAgent"),
    width: 320,
    sort: true,
    transform: ellipsisCell,
  },
  {
    title: "Company",
    key: nameOf<ActionLog>("companyName"),
    width: 320,
    sort: true,
  },
  {
    title: "Tenant",
    key: nameOf<ActionLog>("tenantName"),
    width: 320,
    sort: true,
  },
  {
    title: "Date Time",
    key: nameOf<ActionLog>("timestamp"),
    width: 320,
    sort: true,
    format: "DD/MM/YYYY HH:mm",
  },
  {
    title: "User Id",
    key: nameOf<ActionLog>("userUid"),
    width: 320,
    sort: true,
  },
  {
    title: "Company Id",
    key: nameOf<ActionLog>("companyUid"),
    width: 320,
    sort: true,
  },
  {
    title: "Tenant Id",
    key: nameOf<ActionLog>("tenantUid"),
    width: 320,
    sort: true,
  },
];

const actionLogsQueryMappings: QueryFieldMapping[] = [
  { formField: "actionDescription", operation: QueryOperation.Contains },
  { formField: "user", queryField: "userUserName", operation: QueryOperation.Equal, transform: comboboxDisplay },
  { formField: "company", queryField: "companyName", operation: QueryOperation.Equal, transform: comboboxDisplay },
  { formField: "tenant", queryField: "tenantName", operation: QueryOperation.Equal, transform: comboboxDisplay },
  { formField: "userUid", operation: QueryOperation.Equal },
  { formField: "companyUid", operation: QueryOperation.Equal },
  { formField: "tenantUid", operation: QueryOperation.Equal },
  { formField: "ipAddress", operation: QueryOperation.Equal },
  { formField: "userAgent", operation: QueryOperation.Contains },
  { formField: "timestamp", operation: QueryOperation.Between, transform: dateRangeValue },
];

export const mapActionLogsViewQuery = (values: ActionLogsForm): SearchQuery =>
  buildSearchQuery(values, actionLogsQueryMappings);

// Factory so the User dropdown can scope to the selected Company (soft scope; platform-wide until a company is set).
export const actionLogsSearchSchema = (
  form: FormInstance<any>,
): DynamicSchema => ({
  groups: [
    {
      controls: [
        { type: "textbox", name: "actionDescription", label: "Action Description" },
        { type: "combobox", name: "company", label: "Company", request: createSearchRequest(getCompanies, "name") },
        {
          type: "combobox",
          name: "user",
          label: "User Name",
          dependsOn: ["company"],
          request: createScopedFilterRequest(getPlatformUsers, "userName", () => [
            ["companyUid", form.getFieldValue("company")?.value],
          ]),
        },
        { type: "combobox", name: "tenant", label: "Tenant", request: createSearchRequest(getTenants, "name") },
        { type: "textbox", name: "userUid", label: "User Id" },
        { type: "textbox", name: "companyUid", label: "Company Id" },
        { type: "textbox", name: "tenantUid", label: "Tenant Id" },
        { type: "textbox", name: "ipAddress", label: "IP Address" },
        { type: "textbox", name: "userAgent", label: "User Agent" },
        { type: "rangetimepicker", name: "timestamp", label: "Date Time" },
      ],
    },
  ],
});
